import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LoadingScreen from "@/components/LoadingScreen";
import AdminLayout from "@/components/layouts/AdminLayout";
import TrainerLayout from "@/components/layouts/TrainerLayout";
import Layout from "@/components/ui/Layout";
import { ROLES } from "@/constants/roles";
import { UserRole } from "@/types/user";
import { getTrainerActiveCohort } from "@/utils/session";

interface GuardedRouteProps {
  path: string;
  roles: Partial<Record<UserRole, JSX.Element>>;
}

const GuardedRoute: React.FC<GuardedRouteProps> = ({ path, roles }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const ComponentForRole = roles[currentUser.role as UserRole];

  if (!ComponentForRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Determine the appropriate layout based on the user's role
  // Trainers viewing a course as participant get the user Layout
  // This includes /course/:cohortId and sub-pages like /transaction-summary, /financial-statements
  const trainerParticipantPages = ["/transaction-summary", "/financial-statements", "/learning-area"];
  const isTrainerCourseView =
    currentUser.role === ROLES.TRAINER &&
    (path.startsWith("/course/") ||
      (trainerParticipantPages.includes(path) && !!getTrainerActiveCohort()));

  let LayoutComponent: React.ComponentType<any>;
  if (isTrainerCourseView) {
    LayoutComponent = Layout;
  } else {
    switch (currentUser.role) {
      case ROLES.ADMIN:
        LayoutComponent = AdminLayout;
        break;
      case ROLES.USER:
        LayoutComponent = Layout;
        break;
      case ROLES.TRAINER:
        LayoutComponent = TrainerLayout;
        break;
      default:
        return <Navigate to="/unauthorized" replace />;
    }
  }

  return (
    <LayoutComponent showSidebar={true}>
      {ComponentForRole}
    </LayoutComponent>
  );
};

export default GuardedRoute;
